import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Insertion, LayoutModel, ModelCategory, ProjectLayoutModel } from '@paragon/contract-library';

import { ComponentDesignBulkMovement } from '..';
import { componentDesignsDeletedWithInsertions, projectSynchronized } from '../layoutModelsSlice';
import {
  createInsertionChangeTracker,
  InsertionChangeTracker,
  recordInsertionCreation,
  recordInsertionRemoval,
  recordInsertionSynchronized,
} from './insertionChangeTracker';
import {
  createPatch,
  createPatchTracker,
  PatchTracker,
  recordChangedModel,
  recordCreatedModel,
  recordModelSaved,
} from './patchTracker';

export interface ChangeTrackerState {
  modifiedModels: PatchTracker;
  modifiedInsertions: InsertionChangeTracker;
}

const initialState: ChangeTrackerState = {
  modifiedModels: createPatchTracker(),
  modifiedInsertions: createInsertionChangeTracker(),
};

const changeTrackerSlice = createSlice({
  name: 'changeTracker',
  initialState,
  reducers: {
    insertionSetQuantity: (
      state,
      action: PayloadAction<{
        insertion: Insertion;
        change: number;
      }>,
    ) => {
      const { payload } = action;

      if (payload.change > 0) {
        recordInsertionCreation(state.modifiedInsertions, payload.insertion, payload.change);
      } else {
        recordInsertionRemoval(state.modifiedInsertions, payload.insertion, Math.abs(payload.change));
      }
    },
    modelPropertyChanged: (
      state,
      action: PayloadAction<{
        modelGuids: ReadonlyArray<string>;
        propertyName: string;
        newValue: any;
      }>,
    ) => {
      for (const modelGuid of action.payload.modelGuids) {
        recordChangedModel(
          state.modifiedModels,
          modelGuid,
          createPatch(action.payload.propertyName, action.payload.newValue),
        );
      }
    },
    componentDesignsMoved: (state, action: PayloadAction<ComponentDesignBulkMovement>) => {
      const { payload } = action;

      for (const movedInsertions of Object.values(payload.insertionsByProject)) {
        for (const movedInsertion of movedInsertions) {
          recordInsertionRemoval(state.modifiedInsertions, movedInsertion);
          recordInsertionCreation(state.modifiedInsertions, {
            ...movedInsertion,
            parentGuid: payload.targetContainerGuid,
          });
        }
      }
    },
    layoutModelCreated: (
      state,
      action: PayloadAction<{
        projectGuid: string;
        parentGuid: string;
        model: LayoutModel;
      }>,
    ) => {
      const { payload } = action;

      if (payload.model.category !== ModelCategory.ComponentDesign) {
        recordCreatedModel(state.modifiedModels, payload.model);
      }

      const insertion: Insertion = {
        type: 'HierarchyModel',
        parentGuid: payload.parentGuid,
        objectGuid: payload.model.guid,
      };

      recordInsertionCreation(state.modifiedInsertions, insertion);
    },
    layoutModelsCreated: (
      state,
      action: PayloadAction<{
        projectGuid: string;
        models: LayoutModel[];
        insertions: readonly Insertion[];
      }>,
    ) => {
      for (const model of action.payload.models) {
        if (model.category !== ModelCategory.ComponentDesign) {
          recordCreatedModel(state.modifiedModels, model);
        }
      }

      for (const insertion of action.payload.insertions) {
        recordInsertionCreation(state.modifiedInsertions, insertion);
      }
    },
    projectCreated: (state, action: PayloadAction<{ model: ProjectLayoutModel }>) => {
      recordCreatedModel(state.modifiedModels, action.payload.model);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      for (const removedInsertion of action.payload.insertions) {
        recordInsertionRemoval(state.modifiedInsertions, removedInsertion);
      }
    });

    builder.addCase(projectSynchronized, (state, action) => {
      for (const { changes } of Object.values(action.payload)) {
        if (!changes) continue;

        recordModelSaved(state.modifiedModels, changes.projectGuid);

        const savedModelGuids = ([] as string[])
          .concat(changes.createdModels.map((model) => model.guid))
          .concat(Object.keys(changes.updatedModels))
          .concat([changes.projectGuid]);

        for (const modelGuid of savedModelGuids) {
          recordModelSaved(state.modifiedModels, modelGuid);
        }

        for (const insertion of changes.createdInsertions) {
          recordInsertionSynchronized(state.modifiedInsertions, insertion);
        }

        for (const insertion of changes.removedInsertions) {
          recordInsertionSynchronized(state.modifiedInsertions, insertion);
        }
      }
    });
  },
});

export const {
  insertionSetQuantity,
  modelPropertyChanged,
  componentDesignsMoved,
  layoutModelCreated,
  layoutModelsCreated,
  projectCreated,
} = changeTrackerSlice.actions;
export const changeTracker = changeTrackerSlice.reducer;
